// `biu_token` is a one-time billing credential. dd-trace's default query obfuscator redacts
// `token` and leaves `biu_token`, so anything copied into Datadog or Bugsnag has to drop
// the value first. The request that hits the API keeps the original URL.
// Keep this replacement in sync with preload.ts, DatadogRumScript, and
// packages/stability/src/bugsnag/redactBiut.ts.

export default function redactBiut(value: string): string {
  if (!/biu_token=/i.test(value)) {
    return value;
  }

  // Stop before whitespace or a sentence period so "…?biu_token=public.secret. Status code: 500"
  // keeps the status text. A second pass must not treat "<redacted>" as another secret.
  return value.replace(
    /(^|[?&#])biu_token=(?!<redacted>)(?:[^&#\s.]|\.(?!\s|$))*/gi,
    (_match, delimiter: string) => `${delimiter}biu_token=<redacted>`
  );
}

export function redactBiutInValue(value: unknown, seen: WeakSet<object> = new WeakSet(), depth = 0): void {
  if (depth > 6 || value === null || typeof value !== 'object') {
    return;
  }

  if (seen.has(value)) {
    return;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const entry = value[index];

      if (typeof entry === 'string') {
        value[index] = redactBiut(entry);
      } else {
        redactBiutInValue(entry, seen, depth + 1);
      }
    }

    return;
  }

  const record = value as Record<string, unknown>;

  for (const key of Object.keys(record)) {
    const entry = record[key];

    if (typeof entry === 'string') {
      record[key] = redactBiut(entry);
    } else {
      redactBiutInValue(entry, seen, depth + 1);
    }
  }
}
